<script lang="ts" module>
  import api from "$shared/lib/api-parkito2.svelte.ts";
  import { type Result } from "$shared/lib/result";

  export type DebtorFormOutput = { type: "new"; debtor: DebtorData } | {
    type: "existing";
    debtorId: string;
    editDebtor: DebtorData | OldDebtorData | null;
  };

  export async function createOrGetDebtorId(
    form: DebtorFormOutput,
  ): Promise<Result<string, string>> {
    if (form.type === "new") {
      const debtorRes = await api.debtorCreate({ body: form.debtor });
      if (debtorRes.data) {
        return ok(debtorRes.data.id);
      } else {
        return err(new String(debtorRes.error).toString());
      }
    } else {
      if (form.editDebtor !== null) {
        api.debtorPut({
          body: form.editDebtor,
          path: { id: form.debtorId },
        });
      }
      return ok(form.debtorId);
    }
  }
</script>

<script lang="ts">
  import {
    type Debtor as OldDebtor,
    type DebtorAccess as OldDebtorAccess,
    type DebtorData as OldDebtorData,
  } from "../../../../apis/parkito";
  import {
    type Debtor,
    type DebtorAccess,
    type DebtorData,
  } from "../../../../apis/parkito2";
  import { Form, FormInput } from "../../../lib/validation_next";
  import { err, ok } from "$shared/lib/result";
  import DebtorAdd from "../../../../projects/dpo/components/debtors/DebtorAdd.svelte";
  import DebtorEdit from "../../../../projects/dpo/components/debtors/DebtorEdit.svelte";
  import Separator from "../../Separator.svelte";
  import ValSelect from "../../ValSelect.svelte";
  import { writable } from "svelte/store";
  import DebtorSelector from "../../../../projects/dpo/components/debtors/DebtorSelector.svelte";

  // Value from subrouter Root.svelte
  type Props = {
    selectableDebtors?: Array<DebtorAccess | OldDebtorAccess>;
    debtorOutput?: FormInput<DebtorFormOutput>;
    title?: string | false;
  };

  const {
    selectableDebtors,
    debtorOutput,
    title = "Parkör (avtalstagare)",
  }: Props = $props();

  // If the debtor is selected from the DebtorSelector component
  const selectedDebtor = writable<Debtor | OldDebtor | null>();
  const selectedDebtorData = Form.map<
    Debtor | null,
    DebtorData | OldDebtorData
  >(
    Form.fromStore(selectedDebtor),
    (debtor: Debtor | null) => {
      if (debtor === null) {
        return {
          hasChanged: true,
          res: err<DebtorData, string>("Ingen vald parkör"),
        };
      } else {
        return {
          hasChanged: true,
          res: ok<DebtorData, string>(debtor),
        };
      }
    },
  );
  let debtorEditComp: DebtorEdit | undefined = $state();
  selectedDebtorData.output.subscribe((data) => {
    if (debtorEditComp) {
      debtorEditComp.updateDebtorData(data.res.unwrapOr(undefined));
    }
  });
  const editedDebtor = Form.input<DebtorData>();
  editedDebtor.subscribeTo(selectedDebtorData.output);

  // Debtor variant select
  const debtorVariantSelectItems = [
    {
      label: "Ny parkör",
      value: "new",
      disabled: false,
    },
    {
      label: "Existerande parkör",
      value: "existing",
      disabled: false,
    },
  ];
  const debtorVariantSelect = Form.multiSelect(
    debtorVariantSelectItems,
    debtorVariantSelectItems[
      selectableDebtors && selectableDebtors.length > 0 ? 1 : 0
    ],
  );

  const debtorSelectItems =
    selectableDebtors && selectableDebtors.length > 0
      ? selectableDebtors.map((debtor) => {
        return {
          label: `${debtor.debtor.name} - ${debtor.debtor.contactEmail}`,
          value: debtor.debtor.id,
          disabled: false,
        };
      })
      : [
        {
          label: "Inga tillgängliga parkörer",
          value: "null",
          disabled: true,
        },
      ];
  const debtorSelect = Form.multiSelect(
    debtorSelectItems,
    debtorSelectItems[0],
  );

  debtorVariantSelect.subscribe((variant) => {
    if (variant.res.isErr()) {
      return;
    }

    if (variant.res.value === "new") {
      $selectedDebtor = null;
    } else if (variant.res.value === "existing") {
      const debtorId = $debtorSelect.res.unwrapOr(null);
      $selectedDebtor = selectableDebtors?.find(
        (d) => d.debtor.id === debtorId,
      )?.debtor || null;
    }
  });

  debtorSelect.subscribe((debtorSelect) => {
    const debtorId = debtorSelect.res.unwrapOr(null);
    $selectedDebtor = selectableDebtors?.find(
      (d) => d.debtor.id === debtorId,
    )?.debtor || null;
  });

  const newDebtorForm = Form.input<DebtorData>();
  const entireForm = Form.oneOf<DebtorFormOutput>(debtorVariantSelect, {
    new: Form.nest(Form.object({
      type: Form.fixed("new"),
      debtor: newDebtorForm,
    })),
    existing: Form.nest(Form.object({
      type: Form.fixed("existing"),
      debtorId: debtorSelect,
      editDebtor: editedDebtor,
    })),
  });

  if (debtorOutput) debtorOutput.subscribeTo(entireForm);
</script>

<div>
  {#if title}
    <h3 class="text-[15px] mb-[16px]">{title}</h3>
  {/if}
  <div
    class="w-full min-[840px]:columns-2 items-start columns-1 max-mobile:px-[8px]"
  >
    <ValSelect
      name="sid"
      field={debtorVariantSelect}
      customClass="py-[8px]"
      customClassLabel={"pl-[16px]"}
    />
  </div>

  {#if     $debtorVariantSelect.res.isOk() &&
      $debtorVariantSelect.res.value === "new"}
    <!-- Debtor form data comes with setContext -->
    <Separator customClass="my-[24px]" />
    <DebtorAdd
      debtorOutput={newDebtorForm}
      requestClose={null}
      debtorData={null}
      buttonsInvisible={true}
    />
  {:else if     $debtorVariantSelect.res.isOk() &&
      $debtorVariantSelect.res.value === "existing"}
    {#if !selectableDebtors}
      <!-- Any debtor -->
      <Separator customClass="my-[24px]" />
      <DebtorSelector
        label="Parkör (avtalstagare)"
        preSelectedDebtorId={undefined}
        bind:selectedDebtor={$selectedDebtor}
      />
    {:else}
      <!-- One of the existing debtors connected to the current user -->
      <ValSelect
        name="selected-existing-debtor"
        label="Ange existerande parkör (avtalstagare)"
        field={debtorSelect}
        customClass="py-[8px] max-w-[480px]"
      />

      {#if $selectedDebtor}
        <DebtorEdit
          bind:this={debtorEditComp}
          debtor={editedDebtor}
          buttonInvisible={true}
        />
      {/if}
    {/if}
  {/if}

  <Separator customClass="my-[24px]" />
</div>
